import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  SparklesIcon, 
  DocumentTextIcon,
  UserIcon,
  Cog6ToothIcon,
  ArrowRightIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { Customer } from '../../types/customer';
import { Quotation } from '../../types/quotation';
import { Template } from '../../types/template';
import { Product } from '../../types/product';
import { useTemplates } from '../../hooks/useTemplates';
import { useCustomers } from '../../hooks/useCustomers';
import { useProducts } from '../../hooks/useProducts';
import { useDocumentStorage } from '../../hooks/useDocumentStorage';
import { useDocumentStorageCategories } from '../../hooks/useDocumentStorageCategories';
import { useDocumentFolders } from '../../hooks/useDocumentFolders';
import { useDocumentVariables } from '../../hooks/useDocumentVariables';
import VariableManager from './VariableManager';
import CreateTemplateModal from '../templates/CreateTemplateModal';
import VariablePreviewPopup from './VariablePreviewPopup';
import { useStorage } from '../../hooks/useStorage';
import { DocGenerator } from '../../utils/docGenerator';
import { productToDocumentVariables } from '../../utils/productToVariables';
import { customerToKeyValuePairs } from '../../utils/customerDataMapper';
import { quotationToKeyValuePairs } from '../../utils/quotationDataMapper';
import { generateDocumentData } from '../../utils/documentDataGenerator';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { ALL_VARIABLES, VARIABLE_CATEGORIES } from '../../utils/variableRegistry';
import toast from 'react-hot-toast';

interface ModernDocumentGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentDataProvider?: Quotation | Customer | null;
} 

type Step = 'customer' | 'product' | 'template' | 'variables' | 'format' | 'storage' | 'generate' | 'complete';

const ModernDocumentGeneratorModal: React.FC<ModernDocumentGeneratorModalProps> = ({
  isOpen,
  onClose,
  documentDataProvider = null
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState<Step>('customer');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    documentDataProvider && 'companyName' in documentDataProvider ? documentDataProvider as Customer : null
  );
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [documentData, setDocumentData] = useState<Record<string, string>>({});
  const [selectedFormats, setSelectedFormats] = useState<string[]>(['pdf']);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState<{url: string, fileName: string}[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [variableManagerKey, setVariableManagerKey] = useState(0);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  const { templates, loading: templatesLoading, refreshTemplates } = useTemplates();
  const { customers, loading: customersLoading } = useCustomers();
  const { products, loading: productsLoading } = useProducts();
  const { createDocument } = useDocumentStorage();
  const { categories, createCategory } = useDocumentStorageCategories();
  const { folders } = useDocumentFolders();
  const { uploadFile } = useStorage();
  const { 
    prepareDocumentData, 
    validateValues 
  } = useDocumentVariables({ 
    templateId: selectedTemplate?.id, 
    customerId: selectedCustomer?.id 
  });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (documentDataProvider) {
        if ('companyName' in documentDataProvider) {
          setSelectedCustomer(documentDataProvider as Customer);
          setCurrentStep('template');
        } else {
          // Quotation case - find related customer
          setCurrentStep('template');
        }
      } else {
        setCurrentStep('customer');
      }
      setSelectedTemplate(null);
      setDocumentData({});
      setGeneratedFiles([]);
      setCustomerSearch('');
      setTemplateSearch('');
      setProductSearch('');
      setSelectedProduct(null);
      
      // Reset storage selection
      setSelectedCategoryId('');
      setSelectedFolderId(null);
    }
  }, [isOpen, documentDataProvider]);

  // Update document data when customer or product changes
  useEffect(() => {
    const sources: any = {};
    
    if (selectedCustomer) {
      sources.customer = selectedCustomer;
    }
    
    if (selectedProduct) {
      sources.product = selectedProduct;
    }
    
    if (documentDataProvider && 'quotationNumber' in documentDataProvider) {
      sources.quotation = documentDataProvider;
    }
    
    // Generáljuk az adatokat az új registry alapján
    const generatedData = generateDocumentData(sources);
    
    // Megtartjuk a régi kompatibilitást is
    const legacyCustomerData = selectedCustomer ? customerToKeyValuePairs(selectedCustomer) : {};
    const legacyProductData = selectedProduct ? productToDocumentVariables(selectedProduct) : {};
    
    setDocumentData({
      ...generatedData,
      ...legacyCustomerData,
      ...legacyProductData
    });
  }, [selectedCustomer, selectedProduct, documentDataProvider]);

  // Auto-select category based on template type
  useEffect(() => {
    if (selectedTemplate && !selectedCategoryId) {
      if (selectedTemplate.type === 'contract') {
        const contractCategory = categories.find(cat => cat.name === 'Szerződések');
        if (contractCategory) {
          setSelectedCategoryId(contractCategory.id);
        }
      } else {
        const generatedCategory = categories.find(cat => cat.name === 'Generált dokumentumok');
        if (generatedCategory) {
          setSelectedCategoryId(generatedCategory.id);
        }
      }
    }
  }, [selectedTemplate, categories, selectedCategoryId]);

  // Auto-select folder if none selected (to avoid root folder issue)
  useEffect(() => {
    if (selectedTemplate && !selectedFolderId && folders.length > 0) {
      // Próbáljunk meg találni egy "Generált dokumentumok" mappát
      const generatedFolder = folders.find(folder => folder.name === 'Generált dokumentumok');
      if (generatedFolder) {
        setSelectedFolderId(generatedFolder.id);
        console.log('📁 Automatikusan kiválasztott mappa:', generatedFolder.name);
      } else {
        // Ha nincs, válasszuk az első elérhető mappát
        const firstFolder = folders[0];
        if (firstFolder) {
          setSelectedFolderId(firstFolder.id);
          console.log('📁 Automatikusan kiválasztott első mappa:', firstFolder.name);
        }
      }
    }
  }, [selectedTemplate, folders, selectedFolderId]);

  const steps: { id: Step; title: string; description: string; icon: React.ComponentType<any> }[] = [
    { id: 'customer', title: 'Ügyfél kiválasztása', description: 'Válaszd ki az ügyfelet', icon: UserIcon },
    { id: 'product', title: 'Termék/Csomag', description: 'Opcionális termék kiválasztása', icon: TagIcon },
    { id: 'template', title: 'Sablon kiválasztása', description: 'Válaszd ki a dokumentum sablont', icon: DocumentTextIcon },
    { id: 'variables', title: 'Változók', description: 'Állítsd be a változókat', icon: Cog6ToothIcon },
    { id: 'format', title: 'Formátum', description: 'Válaszd ki a kimeneti formátumot', icon: DocumentArrowDownIcon },
    { id: 'storage', title: 'Tárolás', description: 'Kategória és mappa kiválasztása', icon: SparklesIcon },
    { id: 'generate', title: 'Generálás', description: 'Dokumentum létrehozása', icon: SparklesIcon },
    { id: 'complete', title: 'Kész', description: 'Dokumentum elkészült', icon: CheckIcon }
  ];

  const currentStepIndex = steps.findIndex(step => step.id === currentStep);

  // Szűrt ügyfelek listája
  const filteredCustomers = customers.filter(customer => {
    if (!customerSearch.trim()) return true;
    
    const searchTerm = customerSearch.toLowerCase();
    const companyName = customer.companyName?.toLowerCase() || '';
    const firstName = customer.firstName?.toLowerCase() || '';
    const lastName = customer.lastName?.toLowerCase() || '';
    const email = customer.email?.toLowerCase() || '';
    const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
    
    return companyName.includes(searchTerm) ||
           fullName.includes(searchTerm) ||
           email.includes(searchTerm) ||
           firstName.includes(searchTerm) ||
           lastName.includes(searchTerm);
  });

  // Szűrt sablonok listája
  const filteredTemplates = templates.filter(template => {
    if (!templateSearch.trim()) return true;
    
    const searchTerm = templateSearch.toLowerCase();
    const name = template.name?.toLowerCase() || '';
    const type = template.type?.toLowerCase() || '';
    const description = template.description?.toLowerCase() || '';
    
    return name.includes(searchTerm) ||
           type.includes(searchTerm) ||
           description.includes(searchTerm);
  });

  // Szűrt termékek listája (minden típus)
  const filteredProducts = products.filter(product => {
    
    if (!productSearch.trim()) return true;
    
    const searchTerm = productSearch.toLowerCase();
    const name = product.name?.toLowerCase() || '';
    const code = product.code?.toLowerCase() || '';
    const description = product.description?.toLowerCase() || '';
    const categoryPath = product.category?.fullPath?.toLowerCase() || '';
    
    return name.includes(searchTerm) ||
           code.includes(searchTerm) ||
           description.includes(searchTerm) ||
           categoryPath.includes(searchTerm);
  });

  const getOrCreateGeneratedDocumentsCategory = async () => {
    console.log('🔍 Kategóriák keresése...', categories.length, 'kategória elérhető');
    const existingCategory = categories.find(cat => cat.name === 'Generált dokumentumok');
    
    if (existingCategory) {
      console.log('✅ Meglévő kategória megtalálva:', existingCategory.id, existingCategory.name);
      return existingCategory.id;
    }

    console.log('➕ Új kategória létrehozása: "Generált dokumentumok"');
    try {
      const newCategoryId = await createCategory({
        name: 'Generált dokumentumok',
        description: 'Sablonokból automatikusan generált dokumentumok',
        color: '#8b5cf6',
        icon: 'SparklesIcon'
      });
      console.log('✅ Új kategória létrehozva:', newCategoryId);
      return newCategoryId;
    } catch (error) {
      console.error('❌ Error creating generated documents category:', error);
      return '';
    }
  };

  // Generált dokumentumok mappa létrehozása/keresése
  const getOrCreateGeneratedDocumentsFolder = async () => {
    const existingFolder = folders.find(folder => folder.name === 'Generált dokumentumok');
    
    if (existingFolder) {
      console.log('✅ Meglévő mappa megtalálva:', existingFolder.id, existingFolder.name);
      return existingFolder.id;
    }

    // Itt kellene implementálni az új mappa létrehozását
    // Egyelőre null-t adunk vissza, de később implementálhatjuk
    console.log('⚠️ "Generált dokumentumok" mappa nem található, null-t használunk');
    return null;
  };

  const handleGenerate = async () => {
    if (!selectedTemplate || !selectedCustomer) return;

    setIsGenerating(true);
    setCurrentStep('generate');

    try {
      // Dinamikus változók előkészítése a Registry-ből
      const dynamicDocumentData = generateDocumentData({
        customer: selectedCustomer,
        product: selectedProduct || undefined,
        quotation: documentDataProvider && 'items' in documentDataProvider ? documentDataProvider as Quotation : undefined,
        additionalData: documentData // Kiegészítő értékek
      });

      console.log('Generált dokumentum adatok:', dynamicDocumentData);

      const generatedDocs = await DocGenerator.generateDocuments(
        selectedTemplate,
        [dynamicDocumentData], // Dinamikus adatok használata
        selectedFormats
      );

      const newGeneratedFiles: {url: string, fileName: string}[] = [];

      for (const format of Object.keys(generatedDocs)) {
        for (let i = 0; i < generatedDocs[format].length; i++) {
          const documentBlob = generatedDocs[format][i];
          const fileName = `${selectedTemplate.name}_${selectedCustomer.companyName || selectedCustomer.firstName}_${Date.now()}.${format}`;

          // Upload to storage
          console.log(`📤 Feltöltés: ${fileName} (${documentBlob.size} bytes)`);
          const fileUrl = await uploadFile(
            new File([documentBlob], fileName, {
              type: format === 'docx'
                ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                : 'application/pdf'
            }),
            `customers/${selectedCustomer.id}/documents/${fileName}`
          );
          console.log(`✅ Feltöltés sikeres: ${fileUrl}`);

          if (fileUrl) {
            newGeneratedFiles.push({ url: fileUrl, fileName });

            // Save to document storage
            try {
              console.log('📁 Dokumentum tárolás kezdődik...');
              
              // Felhasználó által kiválasztott kategória használata
              const categoryId = selectedCategoryId;
              console.log('📂 Kiválasztott kategória használata:', categoryId);
              
              if (!categoryId) {
                throw new Error('Nincs kategória kiválasztva a dokumentum tárolásához');
              }

              // Közvetlenül Firebase-be írunk, dupla feltöltés elkerülése érdekében
              
              // Kategória adatok lekérése
              const category = categories.find(cat => cat.id === categoryId);
              
              // Ügyfél név meghatározása
              const customerName = selectedCustomer.companyName || 
                                 `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim();

              const documentData = {
                title: `${selectedTemplate.name} - ${customerName}`,
                description: `Generált dokumentum: ${selectedTemplate.name}`,
                categoryId: categoryId,
                category: category ? {
                  id: categoryId,
                  name: category.name,
                  color: category.color,
                  icon: category.icon
                } : null,
                customerId: selectedCustomer.id,
                customerName: customerName,
                folderId: selectedFolderId,
                tags: [
                  'generált', 
                  selectedTemplate.type === 'contract' ? 'szerződés' : (selectedTemplate.type || 'dokumentum'), 
                  format.toUpperCase()
                ],
                isPublic: false,
                creationDate: new Date(),
                expirationDate: null,
                version: 1,
                status: 'active',
                fileName: fileName,
                fileSize: documentBlob.size,
                fileType: format === 'docx' 
                  ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                  : 'application/pdf',
                filePath: fileUrl,
                downloadUrl: fileUrl,
                createdBy: user?.uid || '',
                createdByName: user?.displayName || user?.email || '',
                organizationId: user?.organizationId || '',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              };

              console.log('📄 Dokumentum adatok Firebase-hez:', {
                title: documentData.title,
                categoryId: documentData.categoryId,
                customerId: documentData.customerId,
                tags: documentData.tags,
                fileName: documentData.fileName,
                fileSize: documentData.fileSize,
                organizationId: documentData.organizationId
              });

              const docRef = await addDoc(collection(db, 'documentStorage'), documentData);
              console.log('✅ Dokumentum sikeresen mentve a Firebase-be, ID:', docRef.id);
              
            } catch (docStorageError: any) {
              console.error('❌ Error saving to document storage:', docStorageError);
              toast.error(`Hiba a dokumentum mentésekor: ${docStorageError?.message || docStorageError}`);
            }
          }
        }
      }

      setGeneratedFiles(newGeneratedFiles);
      setCurrentStep('complete');
      toast.success('Dokumentum sikeresen generálva!');
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error('Hiba a dokumentum generálása során');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleNext = () => {
    const nextStepIndex = currentStepIndex + 1;
    if (nextStepIndex < steps.length) {
      setCurrentStep(steps[nextStepIndex].id);
    }
  };

  const handleBack = () => {
    const prevStepIndex = currentStepIndex - 1;
    if (prevStepIndex >= 0) {
      setCurrentStep(steps[prevStepIndex].id);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'customer': return !!selectedCustomer;
      case 'product': return true; // Opcionális lépés
      case 'template': return !!selectedTemplate;
      case 'variables': return true;
      case 'format': return selectedFormats.length > 0;
      case 'storage': return !!selectedCategoryId;
      case 'generate': return false;
      case 'complete': return false;
      default: return false;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-6xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <SparklesIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Dokumentum Generátor</h2>
                  <p className="text-purple-100 text-sm">Professzionális dokumentumok egyszerűen</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Progress Steps */}
            <div className="mt-6 flex items-center justify-between">
              {steps.map((step, index) => {
                const isActive = step.id === currentStep;
                const isCompleted = index < currentStepIndex;
                const StepIcon = step.icon;

                return (
                  <div key={step.id} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <motion.div
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                          isCompleted
                            ? 'bg-green-500 text-white'
                            : isActive
                            ? 'bg-white text-purple-600'
                            : 'bg-white/20 text-white/60'
                        }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {isCompleted ? (
                          <CheckIcon className="w-5 h-5" />
                        ) : (
                          <StepIcon className="w-5 h-5" />
                        )}
                      </motion.div>
                      <div className="mt-2 text-center">
                        <p className={`text-xs font-medium ${isActive ? 'text-white' : 'text-white/70'}`}>
                          {step.title}
                        </p>
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`w-16 h-0.5 mx-2 ${isCompleted ? 'bg-green-400' : 'bg-white/20'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 min-h-[400px]">
            <AnimatePresence mode="wait">
              {currentStep === 'customer' && (
                <motion.div
                  key="customer"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Válaszd ki az ügyfelet
                    </h3>
                    <div className="text-sm text-gray-500">
                      {filteredCustomers.length} / {customers.length} ügyfél
                    </div>
                  </div>
                  
                  {/* Keresőmező */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 placeholder-gray-400"
                      placeholder="Keresés név, cég vagy email alapján..."
                    />
                    {customerSearch && (
                      <button
                        onClick={() => setCustomerSearch('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-80 overflow-y-auto">
                    {filteredCustomers.length === 0 ? (
                      <div className="col-span-2 text-center py-8 text-gray-500">
                        {customerSearch ? 'Nincs találat a keresési feltételeknek megfelelően' : 'Nincsenek ügyfelek'}
                      </div>
                    ) : (
                      filteredCustomers.map((customer) => (
                      <motion.div
                        key={customer.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          selectedCustomer?.id === customer.id
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                        }`}
                        onClick={() => setSelectedCustomer(customer)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                            <UserIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {customer.companyName || `${customer.firstName} ${customer.lastName}`}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {customer.email}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}

              {currentStep === 'product' && (
                <motion.div
                  key="product"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Termék/Csomag kiválasztása
                    </h3>
                    <div className="text-sm text-gray-500">
                      Opcionális lépés
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start space-x-3">
                      <TagIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                          Termék-alapú dokumentum generálás
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Ha kiválasztasz egy terméket vagy szolgáltatási csomagot, a dokumentum automatikusan kitöltődik 
                          a termék adataival, szolgáltatásaival és árazásával. Ez különösen hasznos szerződések és árajánlatok generálásához.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Keresőmező */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 placeholder-gray-400"
                      placeholder="Keresés név, kód vagy kategória alapján..."
                    />
                    {productSearch && (
                      <button
                        onClick={() => setProductSearch('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      </button>
                    )}
                  </div>

                  {/* Kiválasztott termék megjelenítése */}
                  {selectedProduct && (
                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <TagIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                          <div>
                            <p className="font-medium text-green-900 dark:text-green-100">
                              {selectedProduct.name}
                            </p>
                            <p className="text-sm text-green-700 dark:text-green-300">
                              {selectedProduct.code} • {selectedProduct.category?.fullPath}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedProduct(null)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-80 overflow-y-auto">
                    {filteredProducts.length === 0 ? (
                      <div className="col-span-2 text-center py-8 text-gray-500">
                        {productSearch ? 'Nincs találat a keresési feltételeknek megfelelően' : 'Nincsenek termékek vagy szolgáltatási csomagok'}
                        <div className="mt-2 text-sm">
                          <button
                            onClick={() => setCurrentStep('template')}
                            className="text-purple-600 hover:text-purple-700 underline"
                          >
                            Kihagyás és tovább a sablonokhoz
                          </button>
                        </div>
                      </div>
                    ) : (
                      filteredProducts.map((product) => (
                        <motion.div
                          key={product.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            selectedProduct?.id === product.id
                              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                              : 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                          }`}
                          onClick={() => setSelectedProduct(product)}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              product.type === 'package' 
                                ? 'bg-gradient-to-br from-green-500 to-blue-500' 
                                : product.type === 'service'
                                ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                                : 'bg-gradient-to-br from-orange-500 to-red-500'
                            }`}>
                              <TagIcon className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                  {product.name}
                                </p>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  product.type === 'package' 
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                                    : product.type === 'service'
                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                                    : 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                                }`}>
                                  {product.type === 'package' ? 'Csomag' : product.type === 'service' ? 'Szolgáltatás' : 'Termék'}
                                </span>
                              </div>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {product.code}
                              </p>
                              {product.category?.fullPath && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                  {product.category.fullPath}
                                </p>
                              )}
                              {product.netPrice && (
                                <p className="text-sm font-medium text-green-600 dark:text-green-400 mt-1">
                                  {new Intl.NumberFormat('hu-HU', {
                                    style: 'currency',
                                    currency: 'HUF'
                                  }).format(product.netPrice)}
                                </p>
                              )}
                            </div>
                          </div>
                          {product.description && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
                              {product.description}
                            </p>
                          )}
                        </motion.div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}

              {currentStep === 'template' && (
                <motion.div
                  key="template"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Válaszd ki a sablont
                    </h3>
                    <div className="flex items-center space-x-3">
                      <div className="text-sm text-gray-500">
                        {filteredTemplates.length} / {templates.length} sablon
                      </div>
                      <button
                        onClick={() => setShowCreateTemplate(true)}
                        className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm rounded-lg transition-colors flex items-center space-x-1"
                      >
                        <PlusIcon className="h-4 w-4" />
                        <span>Új sablon</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Keresőmező */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={templateSearch}
                      onChange={(e) => setTemplateSearch(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 placeholder-gray-400"
                      placeholder="Keresés sablon név vagy típus alapján..."
                    />
                    {templateSearch && (
                      <button
                        onClick={() => setTemplateSearch('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-80 overflow-y-auto">
                    {filteredTemplates.length === 0 ? (
                      <div className="col-span-2 text-center py-8">
                        <div className="text-gray-500 mb-4">
                          {templateSearch ? 'Nincs találat a keresési feltételeknek megfelelően' : 'Nincsenek sablonok'}
                        </div>
                        <div className="text-sm text-gray-400 mb-4">
                          Nincs megfelelő sablon? Hozz létre egy újat!
                        </div>
                        <button
                          onClick={() => setShowCreateTemplate(true)}
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center space-x-2 mx-auto"
                        >
                          <PlusIcon className="h-4 w-4" />
                          <span>Új sablon létrehozása</span>
                        </button>
                      </div>
                    ) : (
                      filteredTemplates.map((template) => (
                      <motion.div
                        key={template.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          selectedTemplate?.id === template.id
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                        }`}
                        onClick={() => setSelectedTemplate(template)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                            <DocumentTextIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {template.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {template.type}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}

              {currentStep === 'variables' && (
                <motion.div
                  key="variables"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Dokumentum változók
                    </h3>
                    <div className="flex items-center space-x-2">
                      <VariablePreviewPopup
                        templateId={selectedTemplate?.id}
                        customerId={selectedCustomer?.id}
                        showValues={true}
                        popupTitle="Elérhető változók előnézete"
                      />
                      <span className="text-sm text-gray-500">Változók előnézete</span>
                      <span className="text-xs text-gray-400">•</span>
                      <button
                        onClick={() => setVariableManagerKey(prev => prev + 1)}
                        className="text-xs text-green-600 hover:text-green-700 underline"
                      >
                        🔄 Frissítés
                      </button>
                      <span className="text-xs text-gray-400">•</span>
                      <a 
                        href="/document-variables" 
                        target="_blank"
                        className="text-xs text-blue-600 hover:text-blue-700 underline"
                      >
                        Registry szinkronizálás
                      </a>
                    </div>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {/* Registry alapú változó lista */}
                    <div className="space-y-4">
                      {VARIABLE_CATEGORIES.map(category => {
                        const categoryVariables = ALL_VARIABLES.filter(v => v.category === category.id);
                        if (categoryVariables.length === 0) return null;
                        
                        return (
                          <div key={category.id} className="border rounded-lg overflow-hidden">
                            <div className="px-4 py-3 bg-gray-50 border-b">
                              <h4 className="font-medium text-gray-900 flex items-center">
                                <div 
                                  className="w-3 h-3 rounded-full mr-2"
                                  style={{ backgroundColor: category.color }}
                                />
                                {category.name} ({categoryVariables.length})
                              </h4>
                            </div>
                            <div className="p-4 space-y-2">
                              {categoryVariables.map(variable => (
                                <div key={variable.key} className="flex justify-between items-center py-1">
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-900">
                                      {variable.name}
                                    </span>
                                    <div className="text-xs text-gray-500 font-mono">
                                      {`{{${variable.key}}}`}
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {documentData[variable.key] || variable.defaultValue || 'nincs érték'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}

              {currentStep === 'format' && (
                <motion.div
                  key="format"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Kimeneti formátum
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Válaszd ki, milyen formátumban szeretnéd a dokumentumot generálni.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { format: 'pdf', title: 'PDF', description: 'Hordozható dokumentum formátum', icon: '📄' },
                      { format: 'docx', title: 'Word', description: 'Microsoft Word dokumentum', icon: '📝' }
                    ].map(({ format, title, description, icon }) => (
                      <motion.div
                        key={format}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          selectedFormats.includes(format)
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                        }`}
                        onClick={() => {
                          if (selectedFormats.includes(format)) {
                            setSelectedFormats(selectedFormats.filter(f => f !== format));
                          } else {
                            setSelectedFormats([...selectedFormats, format]);
                          }
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="text-2xl">{icon}</div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {title}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {description}
                            </p>
                          </div>
                          {selectedFormats.includes(format) && (
                            <CheckIcon className="w-5 h-5 text-purple-500 ml-auto" />
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      💡 <strong>Tipp:</strong> Több formátumot is kiválaszthatsz egyszerre. A dokumentum mindegyik formátumban elkészül.
                    </p>
                  </div>
                </motion.div>
              )}

              {currentStep === 'storage' && (
                <motion.div
                  key="storage"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Tárolás beállítása
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Válaszd ki, hová kerüljön a generált dokumentum
                    </p>
                  </div>

                  {/* Kategória kiválasztás */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Kategória *
                    </label>
                    <div className="grid grid-cols-1 gap-3">
                      {categories.map(category => (
                        <motion.div
                          key={category.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedCategoryId === category.id
                              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                          onClick={() => setSelectedCategoryId(category.id)}
                        >
                          <div className="flex items-center space-x-3">
                            <div 
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: category.color }}
                            />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 dark:text-gray-100">
                                {category.name}
                              </p>
                              {category.description && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {category.description}
                                </p>
                              )}
                            </div>
                            {selectedCategoryId === category.id && (
                              <CheckIcon className="w-5 h-5 text-purple-500" />
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Mappa kiválasztás (opcionális) */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Mappa (opcionális)
                    </label>
                    <select
                      value={selectedFolderId || ''}
                      onChange={(e) => setSelectedFolderId(e.target.value || null)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="">Nincs mappa (gyökér)</option>
                      {folders.map(folder => (
                        <option key={folder.id} value={folder.id}>
                          {folder.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      💡 <strong>Tipp:</strong> A kategória kötelező, de a mappa opcionális. Ha nem választasz mappát, a dokumentum a gyökérbe kerül.
                    </p>
                  </div>
                </motion.div>
              )}

              {currentStep === 'generate' && (
                <motion.div
                  key="generate"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="flex flex-col items-center justify-center py-12"
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mb-4"
                  >
                    <SparklesIcon className="w-8 h-8 text-white" />
                  </motion.div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Dokumentum generálása...
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Kérlek várj, amíg elkészítjük a dokumentumot
                  </p>
                </motion.div>
              )}

              {currentStep === 'complete' && (
                <motion.div
                  key="complete"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="text-center py-8"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", duration: 0.5 }}
                    className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <CheckIcon className="w-8 h-8 text-white" />
                  </motion.div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Dokumentum elkészült!
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-6">
                    A dokumentum sikeresen generálva és mentve a Dokumentumtárba
                  </p>
                  
                  {generatedFiles.length > 0 && (
                    <div className="space-y-2">
                      {generatedFiles.map((file, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <DocumentTextIcon className="w-5 h-5 text-gray-500" />
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {file.fileName}
                            </span>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => window.open(file.url, '_blank')}
                              className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            <a
                              href={file.url}
                              download={file.fileName}
                              className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                            >
                              <ArrowDownTrayIcon className="w-4 h-4" />
                            </a>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
            <button
              onClick={currentStep === 'complete' ? onClose : handleBack}
              disabled={currentStepIndex === 0 && currentStep !== 'complete'}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {currentStep === 'complete' ? 'Bezárás' : 'Vissza'}
            </button>

            <div className="flex space-x-3">
              {currentStep === 'format' && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleGenerate}
                  disabled={selectedFormats.length === 0}
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2"
                >
                  <SparklesIcon className="w-4 h-4" />
                  <span>Generálás</span>
                </motion.button>
              )}

              {canProceed() && currentStep !== 'format' && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleNext}
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2"
                >
                  <span>Tovább</span>
                  <ArrowRightIcon className="w-4 h-4" />
                </motion.button>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Create Template Modal */}
      <CreateTemplateModal
        isOpen={showCreateTemplate}
        onClose={() => {
          setShowCreateTemplate(false);
          // Frissítjük a sablonok listáját
          refreshTemplates();
        }}
      />
    </div>
  );
};

export default ModernDocumentGeneratorModal;
